import axios from 'axios';
import { Eye, EyeOff } from 'lucide-react';
import { useContext, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import { useNavigate } from 'react-router-dom';
import '../../../../assets/css/customer/publicCustomerCss/CustomerFormCss/customerSignupForm.css';
import AppContext from '../../../../context/AppContext';

const CustomerSignupForm = () => {  
  const { baseUrl, validateField } = useContext(AppContext);
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [role, setRole] = useState("tourist");
  
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  const [nameError, setNameError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");

  const handleVendorRegistration = (formData) => {
    axios.post(`${baseUrl}/vendor/register`, formData, {
      headers: { "Content-Type": "multipart/form-data" }
    })
    .then(response => {
      setIsLoading(false);
      
      if (response.data.success) {
        const vendorId = response.data.vendor.id;

        localStorage.setItem("vendorEmail", email);
        localStorage.setItem("vendorPassword", password);
        localStorage.setItem("userRole", "vendor");
        localStorage.setItem("vendorId", vendorId);
        localStorage.setItem("userName", name);
        
        setMessage("Registration successful! Redirecting to login...");
        setMessageType("success");
        setTimeout(() => navigate('/auth/login'), 1500);
      } else {
        setMessage(response.data.message || "Registration failed");
        setMessageType("error");
      }
    })
    .catch(error => {
      console.log(error);
      setMessage(error.response?.data?.message || "Registration failed");
      setMessageType("error");
      setIsLoading(false);
    });
  };

  const handleCustomerRegistration = (formData) => {
    formData.append('cus_username', name);
    formData.append('cus_email', email);
    formData.append('cus_password', password);
    formData.append('confirm_password', confirmPassword);
    formData.append('cus_role', 'customer');

    axios.post(`${baseUrl}/customer/register`, formData, {
      headers: { "Content-Type": "multipart/form-data" }
    })
    .then(response => {
      setIsLoading(false);
      
      if (response.data.success) {
        const customer = response.data.customer;

        localStorage.setItem("customerEmail", email);
        localStorage.setItem("customerPassword", password);
        localStorage.setItem("userRole", "customer");
        localStorage.setItem("customerId", customer.id);
        localStorage.setItem("userName", customer.cus_username);
        
        setMessage("Registration successful! Redirecting to login...");
        setMessageType("success");
        setTimeout(() => navigate('/auth/login'), 1500);
      } else {
        setMessage(response.data.message || "Registration failed");
        setMessageType("error");
      }
    })
    .catch(error => {
      console.log(error);
      setMessage(error.response?.data?.message || "Registration failed");
      setMessageType("error");
      setIsLoading(false);
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const nameErr = validateField('name', name);
    const emailErr = validateField('email', email);
    const passwordErr = validateField('password', password);

let confirmErr = "";
if (!confirmPassword) {
  confirmErr = "Please confirm your password";
} else if (password !== confirmPassword) {
  confirmErr = "Password and confirm password do not match!";
}

setNameError(nameErr);
setEmailError(emailErr);
setPasswordError(passwordErr);
setConfirmPasswordError(confirmErr);

if (nameErr || emailErr || passwordErr || confirmErr) {
  if (confirmErr) setMessage(confirmErr);
  else if (passwordErr) setMessage(passwordErr);
  setMessageType("error");
  return;
}
    setIsLoading(true);
    setMessage("Creating account...");
    setMessageType("success");

    const formData = new FormData();

    if (role === 'vendor') {
      formData.append('name', name);
      formData.append('email', email);
      formData.append('password', password);
      formData.append('confirm_password', confirmPassword);
      formData.append('role', 'vendor');
      
      handleVendorRegistration(formData);
    } 
    else if (role === 'tourist') {
      handleCustomerRegistration(formData);
    }
  };

  return (
    <Form 
      id="ta-signupForm" 
      className="ta-signup-form" 
      onSubmit={handleSubmit}
    >
      <h2 className="ta-form-title">Create Account</h2>
      
      <Form.Group controlId="signupName" className="ta-form-group">
        <input
          type="text"
          className={`ta-form-control ${nameError ? 'is-invalid' : ''}`}
          placeholder="Full Name"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            setNameError('');
          }}
          disabled={isLoading}
          required
        />
        {nameError && <div className="invalid-feedback d-block">{nameError}</div>}
      </Form.Group>
      
      <Form.Group controlId="signupEmail" className="ta-form-group">
        <input
          type="email"
          className={`ta-form-control ${emailError ? 'is-invalid' : ''}`}
          placeholder="Email address"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setEmailError('');
          }}
          disabled={isLoading}
          required
        />
        {emailError && <div className="invalid-feedback">{emailError}</div>}
      </Form.Group>
      
      <Form.Group controlId="signupPassword" className="ta-form-group">
        <div className="ta-password-wrapper">
          <input
            type={showPassword ? 'text' : 'password'}
            className={`ta-password-input ${passwordError ? 'is-invalid' : ''}`}
            placeholder="Password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setPasswordError('');
              setConfirmPasswordError('');
            }}
            disabled={isLoading}
            required
            minLength="6"
          />
          <button 
            type="button" 
            className="ta-eye-btn" 
            onClick={() => setShowPassword(!showPassword)}
            disabled={isLoading}
          >
            {showPassword ? <Eye size={18} /> : <EyeOff size={18} />}
          </button>
        </div>
        {passwordError && <div className="invalid-feedback">{passwordError}</div>}
      </Form.Group>

      <Form.Group controlId="signupConfirmPassword" className="ta-form-group">
        <div className="ta-password-wrapper">
          <input
            type={showConfirmPassword ? 'text' : 'password'}
            className={`ta-password-input ${confirmPasswordError ? 'is-invalid' : ''}`}
            placeholder="Confirm Password"
            value={confirmPassword}
            onChange={(e) => {
              setConfirmPassword(e.target.value);
              setConfirmPasswordError('');
            }}
            disabled={isLoading}
            required
          />
          <button 
            type="button" 
            className="ta-eye-btn" 
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            disabled={isLoading}
          >
            {showConfirmPassword ? <Eye size={18} /> : <EyeOff size={18} />}
          </button>
        </div>
        {confirmPasswordError && <div className="invalid-feedback">{confirmPasswordError}</div>}
      </Form.Group>
      
      <Form.Group controlId="signupRole" className="ta-form-group">
        <Form.Select
          className="ta-form-control"
          value={role}
          onChange={(e) => setRole(e.target.value)}
          disabled={isLoading}
        >
          <option value="tourist">Tourist</option>
          <option value="vendor">Vendor</option>
        </Form.Select>
      </Form.Group>

      <Button type="submit" className="ta-submit-btn" disabled={isLoading}>
        {isLoading ? (
          <>
            <span
              className="spinner-border spinner-border-sm me-2"
              role="status"
              aria-hidden="true"
            ></span>
            Creating Account...
          </>
        ) : (
          "Create Account"
        )}
      </Button>
      
      {message && <p className={`ta-message ta-${messageType}`}>{message}</p>}
    </Form>
  );
};

export default CustomerSignupForm;